import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const StantonHomesNearWestminsterPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Stanton&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching Stanton properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Stanton CA Homes Near Westminster | Affordable Living | Truc Doan</title>
        <meta name="description" content="Stanton CA homes for sale near Westminster and Little Saigon. Affordable family homes from $500K-$850K with easy commute to Westminster. Truc Doan is your local Stanton real estate expert." />
        <meta name="keywords" content="Stanton CA homes for sale, Stanton real estate, homes near Westminster CA, affordable homes Orange County, Stanton family homes, Stanton CA realtor, near Little Saigon, Truc Doan Stanton, Stanton neighborhoods, buy home Stanton CA" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/stanton-homes-near-westminster`} />
        <meta property="og:title" content="Stanton CA Homes Near Westminster | Affordable Living | Truc Doan" />
        <meta property="og:description" content="Affordable Stanton CA homes near Westminster & Little Saigon. Median ~$700K, family neighborhoods, easy commute. Truc Doan, your local expert." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/stanton-homes-near-westminster`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Stanton, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": "+1-818-818-0989",
            "email": "doanestate@gmail.com",
            "url": `${SEO_CONFIG.DOMAIN}/stanton-homes-near-westminster`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Find affordable homes in Stanton CA near Westminster and Little Saigon. Truc Doan helps families find homes in Stanton's friendly neighborhoods.",
            "areaServed": {
              "@type": "City",
              "name": "Stanton, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Stanton CA Homes Near Westminster – Affordable Family Living</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Discover affordable homes in Stanton with easy access to Westminster, Little Saigon, and all of Orange County. Median home price ~$700K.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Why Choose Stanton?</h2>
          <p>Stanton offers some of the most affordable single-family homes in Orange County while sitting just minutes from Westminster and Little Saigon. Families love Stanton for its quiet neighborhoods, community parks, and convenient location between major freeways connecting you to all of OC and LA County.</p>
          <p>With a median home price around $700,000, Stanton provides exceptional value compared to neighboring cities. You get more space for your money while maintaining easy access to Westminster's Vietnamese restaurants, shops, and cultural amenities just a short drive away.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Stanton Neighborhood Highlights</h2>
          <p>Stanton's family-friendly neighborhoods offer the perfect balance of affordability, convenience, and community.</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Affordable Family Homes</h3>
              <p>Single-family homes ranging from $500K-$850K with generous lot sizes, garages, and yards – significantly more affordable than neighboring Westminster and Garden Grove.</p>
            </div>
            <div className="amenity">
              <h3>Minutes from Little Saigon</h3>
              <p>Just 5-10 minutes from Westminster's Bolsa Avenue, giving you full access to Vietnamese restaurants, markets, and cultural centers without the higher home prices.</p>
            </div>
            <div className="amenity">
              <h3>Family-Friendly Parks</h3>
              <p>Stanton Central Park, Harry M. Dotson Park, and multiple community spaces offer playgrounds, sports fields, and recreation programs for families.</p>
            </div>
            <div className="amenity">
              <h3>Commute Convenience</h3>
              <p>Easy access to the 5, 22, and 39 freeways connects you to employment centers in Irvine, Anaheim, Long Beach, and LA. Average commute times under 30 minutes to major job hubs.</p>
            </div>
            <div className="amenity">
              <h3>Growing Community</h3>
              <p>Stanton is investing in community improvements, new retail, and infrastructure upgrades that are driving property values up while homes remain affordable.</p>
            </div>
            <div className="amenity">
              <h3>Schools & Education</h3>
              <p>Served by Magnolia School District and Garden Grove Unified, with Vietnamese language programs and diverse, inclusive school communities.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Stanton Real Estate Market Snapshot</h2>
          <p>Stanton offers one of the best value propositions in Orange County for homebuyers seeking affordability without sacrificing location.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Median Home Price</td><td>~$700,000</td></tr>
              <tr><td>Average Days on Market</td><td>20–30 Days</td></tr>
              <tr><td>Popular Price Range</td><td>$500K–$850K</td></tr>
              <tr><td>Distance to Westminster</td><td>5–10 min drive</td></tr>
              <tr><td>vs Westminster Median</td><td>~$50K less</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Homes for Sale in Stanton</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">{formatPropertyAddress(property)}, CA {property.postal_code}</div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Browse all available Stanton listings by clicking "Search Listings" below.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>Ready to Find Your Stanton Home?</h2>
          <p>Contact Truc Doan today to explore affordable homes in Stanton near Westminster and Little Saigon. Get started with a free consultation.</p>
          <div className="cta-buttons">
            <a href="mailto:doanestate@gmail.com?subject=Stanton Homes Inquiry" className="cta-button primary">Email Truc Doan</a>
            <a href="tel:+1-818-818-0989" className="cta-button secondary">Call +1-818-818-0989</a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>Search Stanton Listings</button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>
          Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.
        </span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default StantonHomesNearWestminsterPage;
